<template>
  <v-dialog
    :model-value="modelValue"
    max-width="720px"
    persistent
    scrollable
    data-testid="cfgv2-export-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="bg-deep-purple text-white d-flex align-center pa-4">
        <v-icon class="mr-2">mdi-export</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">ส่งออกเวอร์ชันคอนฟิกเป็นไฟล์</div>
          <div v-if="configVersionLabel" class="text-caption text-white-50">
            เวอร์ชัน {{ configVersionLabel }}
          </div>
        </div>
        <v-btn
          icon
          variant="text"
          color="white"
          :disabled="busy"
          data-testid="cfgv2-export-close-btn"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- categories + rules travel ALWAYS — the checkboxes below only pick the children -->
        <v-alert
          type="info"
          variant="tonal"
          density="comfortable"
          class="mb-4"
          icon="mdi-package-variant-closed"
          data-testid="cfgv2-export-note"
        >
          หมวดหมู่ (categories) และ registry ของ FieldKey (rules) จะถูกส่งออกไปด้วยเสมอ —
          เลือกด้านล่างเฉพาะชุดสูตร / ชุดคำถามที่ต้องการพาไปพร้อมกัน
        </v-alert>

        <!-- ── ชุดสูตร (Tax Calculate) ─────────────────────────────── -->
        <div class="d-flex align-center flex-wrap ga-2 mb-1">
          <div class="text-subtitle-2 flex-grow-1">
            <v-icon size="small" class="mr-1">mdi-function-variant</v-icon>
            ชุดสูตร (Tax Calculate)
            <span class="text-caption text-medium-emphasis ml-1">
              เลือก {{ selectedCalcIds.length }} / {{ calcList.length }}
            </span>
          </div>
          <v-btn
            size="x-small"
            variant="text"
            :disabled="busy || !calcList.length || selectedCalcIds.length === calcList.length"
            data-testid="cfgv2-export-calc-all"
            @click="selectAll('calc')"
          >
            เลือกทั้งหมด
          </v-btn>
          <v-btn
            size="x-small"
            variant="text"
            :disabled="busy || !selectedCalcIds.length"
            data-testid="cfgv2-export-calc-none"
            @click="selectNone('calc')"
          >
            ไม่เลือก
          </v-btn>
        </div>
        <div class="cfgv2-export-group mb-4">
          <div v-if="!calcList.length" class="text-caption text-medium-emphasis pa-3">
            ไม่มีในเวอร์ชันนี้
          </div>
          <v-checkbox
            v-for="cv in calcList"
            :key="`c-${cv.calcVersionID}`"
            v-model="selectedCalcIds"
            :value="cv.calcVersionID"
            :disabled="busy"
            density="compact"
            hide-details
            color="deep-purple"
            :data-testid="`cfgv2-export-calc-${cv.calcVersionID}`"
            @update:model-value="onTouched"
          >
            <template #label>
              <span class="cfgv2-export-code">{{ cv.versionCode }}</span>
              <v-chip
                v-if="cv.isActive"
                size="x-small"
                color="success"
                variant="flat"
                class="ml-2"
              >
                ACTIVE
              </v-chip>
              <span v-if="isNumber(cv.functionCount)" class="text-caption text-medium-emphasis ml-2">
                {{ cv.functionCount }} สูตร
              </span>
              <span v-if="cv.description" class="text-caption text-medium-emphasis ml-2">
                — {{ cv.description }}
              </span>
            </template>
          </v-checkbox>
        </div>

        <!-- ── ชุดคำถาม (Tax Question) ─────────────────────────────── -->
        <div class="d-flex align-center flex-wrap ga-2 mb-1">
          <div class="text-subtitle-2 flex-grow-1">
            <v-icon size="small" class="mr-1">mdi-comment-question-outline</v-icon>
            ชุดคำถาม (Tax Question)
            <span class="text-caption text-medium-emphasis ml-1">
              เลือก {{ selectedQuestionIds.length }} / {{ questionList.length }}
            </span>
          </div>
          <v-btn
            size="x-small"
            variant="text"
            :disabled="busy || !questionList.length || selectedQuestionIds.length === questionList.length"
            data-testid="cfgv2-export-question-all"
            @click="selectAll('question')"
          >
            เลือกทั้งหมด
          </v-btn>
          <v-btn
            size="x-small"
            variant="text"
            :disabled="busy || !selectedQuestionIds.length"
            data-testid="cfgv2-export-question-none"
            @click="selectNone('question')"
          >
            ไม่เลือก
          </v-btn>
        </div>
        <div class="cfgv2-export-group">
          <div v-if="!questionList.length" class="text-caption text-medium-emphasis pa-3">
            ไม่มีในเวอร์ชันนี้
          </div>
          <v-checkbox
            v-for="qv in questionList"
            :key="`q-${qv.questionVersionID}`"
            v-model="selectedQuestionIds"
            :value="qv.questionVersionID"
            :disabled="busy"
            density="compact"
            hide-details
            color="deep-purple"
            :data-testid="`cfgv2-export-question-${qv.questionVersionID}`"
            @update:model-value="onTouched"
          >
            <template #label>
              <span class="cfgv2-export-code">{{ qv.versionCode }}</span>
              <v-chip
                v-if="qv.isActive"
                size="x-small"
                color="success"
                variant="flat"
                class="ml-2"
              >
                ACTIVE
              </v-chip>
              <span v-if="qv.description" class="text-caption text-medium-emphasis ml-2">
                — {{ qv.description }}
              </span>
            </template>
          </v-checkbox>
        </div>

        <!-- A question set without its formulas: every CALCULATE item lands with no calc version
             to bind to and silently never computes on the other machine. Warn, do not block —
             carrying only the questions is a legitimate (if rare) choice. -->
        <v-alert
          v-if="questionsWithoutCalc"
          type="warning"
          variant="tonal"
          density="comfortable"
          class="mt-4"
          data-testid="cfgv2-export-warning"
        >
          เลือกชุดคำถามโดยไม่เลือกชุดสูตรเลย — ข้อ CALCULATE ในชุดคำถามจะไม่มีสูตรให้อ้าง
          ปลายทางจะไม่คำนวณ
        </v-alert>
      </v-card-text>

      <v-card-actions class="pa-4">
        <span class="text-caption text-medium-emphasis" data-testid="cfgv2-export-summary">
          จะส่งออก: หมวดหมู่ + FieldKey ทั้งหมด · {{ selectedCalcIds.length }} ชุดสูตร ·
          {{ selectedQuestionIds.length }} ชุดคำถาม
        </span>
        <v-spacer />
        <v-btn
          variant="outlined"
          :disabled="busy"
          data-testid="cfgv2-export-cancel-btn"
          @click="close"
        >
          <v-icon start>mdi-close</v-icon>
          ยกเลิก
        </v-btn>
        <v-btn
          color="deep-purple"
          variant="elevated"
          :loading="busy"
          :disabled="!(Number(configVersionId) > 0)"
          data-testid="cfgv2-export-submit-btn"
          @click="submit"
        >
          <v-icon start>mdi-download</v-icon>
          ส่งออก
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Pick WHICH children ride along when a whole config version is exported as one
 * `lhb.taxconfig.v2.bundle` file — the config-level counterpart of the per-row «ส่งออกเป็นไฟล์»
 * on the calc / question rows of `views/TaxConfigurationV2.vue`.
 *
 * Contract with the parent (`views/TaxConfigurationV2.vue`):
 *   - the parent owns the source identity (`configVersionId`), the two candidate lists
 *     (`calcVersions` / `questionVersions` — it must have loaded them BEFORE opening, the
 *     sub-trees are lazy), the API call, the Blob download and the snackbar;
 *   - this dialog only collects the selection and emits `export` with
 *     `{ calcVersionIds, questionVersionIds }`. It does NOT close itself and does NOT call the
 *     API — the parent flips `busy` while it downloads and decides when the dialog goes away.
 *     «ยกเลิก» / «X» still emit `update:modelValue: false` normally.
 *
 * Everything is ticked by default every time the dialog opens (A2): the common case is "move
 * the whole version", and un-ticking is the deliberate act. If the lists arrive AFTER the
 * dialog opened (parent raced the lazy load) the default is re-applied, but never over a
 * selection the user already touched.
 *
 * Categories and rules are NOT a choice — the export endpoint always carries every row of
 * both (inactive included), because the children cross-reference them by FieldKey.
 */
export default {
  name: 'TaxConfigExportV2Dialog',
  props: {
    modelValue: { type: Boolean, default: false },
    /** source config version — the parent posts the selection to `versions/{id}/export` */
    configVersionId: { type: Number, default: null },
    /** shown as the dialog subtitle (e.g. `2568 / V1`) */
    configVersionLabel: { type: String, default: '' },
    /** `CalcVersion[]` of this config version (`calcVersionID`, `versionCode`, `description`, `isActive`, `functionCount`) */
    calcVersions: { type: Array, default: () => [] },
    /** `TaxQuestionVersionV2Dto[]` of this config version (`questionVersionID`, `versionCode`, `description`, `isActive`) */
    questionVersions: { type: Array, default: () => [] },
    /** the parent is calling the export API / building the download — locks the form, spins the button */
    busy: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'export'],
  data() {
    return {
      /** ticked calc version ids (`calcVersionID`) */
      selectedCalcIds: [],
      /** ticked question version ids (`questionVersionID`) */
      selectedQuestionIds: [],
      /** the user changed the selection — stops a late list refresh from re-ticking everything */
      touched: false,
    };
  },
  computed: {
    /** rows with a usable id only — a malformed DTO must not produce an unkeyed checkbox */
    calcList() {
      return (Array.isArray(this.calcVersions) ? this.calcVersions : []).filter(
        (cv) => cv && Number(cv.calcVersionID) > 0,
      );
    },
    questionList() {
      return (Array.isArray(this.questionVersions) ? this.questionVersions : []).filter(
        (qv) => qv && Number(qv.questionVersionID) > 0,
      );
    },
    /** questions ticked, formulas not — the CALCULATE items would have nothing to bind to */
    questionsWithoutCalc() {
      return this.selectedQuestionIds.length > 0 && this.selectedCalcIds.length === 0;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.resetState();
    },
    /** lists that land after opening get the same default — unless the user already chose */
    calcVersions() {
      if (this.modelValue && !this.touched) this.selectAll('calc', { silent: true });
    },
    questionVersions() {
      if (this.modelValue && !this.touched) this.selectAll('question', { silent: true });
    },
  },
  methods: {
    isNumber(v) {
      return typeof v === 'number' && Number.isFinite(v);
    },
    resetState() {
      this.touched = false;
      this.selectAll('calc', { silent: true });
      this.selectAll('question', { silent: true });
    },
    close() {
      if (this.busy) return;
      this.$emit('update:modelValue', false);
    },
    onTouched() {
      this.touched = true;
    },
    selectAll(group, { silent = false } = {}) {
      if (group === 'calc') {
        this.selectedCalcIds = this.calcList.map((cv) => cv.calcVersionID);
      } else {
        this.selectedQuestionIds = this.questionList.map((qv) => qv.questionVersionID);
      }
      if (!silent) this.touched = true;
    },
    selectNone(group) {
      if (group === 'calc') this.selectedCalcIds = [];
      else this.selectedQuestionIds = [];
      this.touched = true;
    },
    submit() {
      if (this.busy || !(Number(this.configVersionId) > 0)) return;
      // Only ids that are still in the lists — a stale tick from a row that vanished on a
      // refresh would otherwise be a 400 from the export endpoint (id not under this version).
      const calcIds = new Set(this.calcList.map((cv) => cv.calcVersionID));
      const questionIds = new Set(this.questionList.map((qv) => qv.questionVersionID));
      this.$emit('export', {
        calcVersionIds: this.selectedCalcIds.filter((id) => calcIds.has(id)),
        questionVersionIds: this.selectedQuestionIds.filter((id) => questionIds.has(id)),
      });
    },
  },
};
</script>

<style scoped>
.text-white-50 {
  opacity: 0.8;
}
.cfgv2-export-group {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 6px;
  padding: 4px 8px;
}
.cfgv2-export-code {
  font-family: 'Roboto Mono', 'SFMono-Regular', Consolas, monospace;
  font-weight: 600;
}
</style>
